import { useState } from "react";
import { HelpCircle, X, ChevronDown } from "lucide-react";

const faqs = [
  {
    question: "How much does a website cost?",
    answer: "Your first website starts at just $500 for a 4 page site (limited intro offer). Websites from $999, business websites from $2,499, and complex web apps from $4,999. We'll give you a detailed quote after understanding your needs."
  },
  {
    question: "How long will my project take?",
    answer: "Most websites take 2-4 weeks. Larger projects like e-commerce or custom software can take 1-3 months. We'll give you a realistic timeline upfront."
  },
  {
    question: "Do I need to pay everything upfront?",
    answer: "No! We split payments - typically 50% to start and 50% when we're done. For bigger projects, we can set up milestone payments."
  },
  {
    question: "Will I be able to update my website myself?",
    answer: "Yes! We build sites with easy-to-use content management systems. We'll also train you on how to make updates yourself."
  },
  {
    question: "What if I don't like the design?",
    answer: "We include revision rounds in every project. We work closely with you throughout the process to make sure you love the final result."
  },
  {
    question: "Do you help with hosting and domains?",
    answer: "Yes! We offer managed hosting for $20/month, including SSL, daily backups, and uptime monitoring. We can also help you register a domain or work with your existing setup."
  },
  {
    question: "What happens after my site launches?",
    answer: "We provide 30 days of free support for any bugs or issues. After that, our Care & Support plan is $50/month for troubleshooting and fixes, or you can reach out for updates anytime."
  },
  {
    question: "Can you help improve my existing website?",
    answer: "Absolutely! Whether you need a refresh, new features, or better performance, we can help improve what you already have."
  }
];

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-white/10 last:border-b-0">
    <button
      onClick={onClick}
      className="w-full py-4 flex items-center justify-between text-left hover:text-[#D4FF00] transition-colors"
    >
      <span className="text-sm font-medium pr-4">{question}</span>
      <ChevronDown 
        size={16} 
        className={`flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180 text-[#D4FF00]' : ''}`}
      />
    </button>
    {isOpen && (
      <div className="pb-4 pr-8">
        <p className="text-xs leading-relaxed" style={{ color: '#A1A1AA' }}>
          {answer}
        </p>
      </div>
    )}
  </div>
);

export default function FloatingFAQ() {
  const [isOpen, setIsOpen] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(0);

  return (
    <>
      {/* FAQ Panel */}
      {isOpen && (
        <div 
          className="fixed bottom-20 right-4 md:bottom-24 md:right-6 w-[calc(100vw-32px)] md:w-[380px] max-h-[70vh] md:max-h-[500px] rounded-2xl overflow-hidden z-50 shadow-2xl"
          style={{ 
            background: 'rgba(15, 15, 17, 0.98)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.1)'
          }}
          data-testid="faq-panel"
        >
          <div className="p-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <h3 className="font-bold text-sm" style={{ fontFamily: '"Clash Display", sans-serif' }}>
              Frequently Asked <span style={{ color: '#D4FF00' }}>Questions</span>
            </h3>
            <button 
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
          
          <div className="p-4 max-h-[400px] overflow-y-auto custom-scrollbar">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-4 right-4 md:bottom-6 md:right-6 w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center z-50 transition-all duration-300 shadow-lg ${
          isOpen ? 'bg-white/10 rotate-180' : 'bg-[#D4FF00] hover:scale-110'
        }`}
        style={{ 
          boxShadow: isOpen ? 'none' : '0 0 30px rgba(212, 255, 0, 0.3)'
        }}
        data-testid="faq-toggle-button"
      >
        {isOpen ? (
          <X size={20} className="text-white md:w-6 md:h-6" />
        ) : (
          <HelpCircle size={20} className="text-black md:w-6 md:h-6" />
        )}
      </button>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.2);
          border-radius: 2px;
        }
      `}</style>
    </>
  );
}
